import {
  back,
  currentPage,
  elapsedMs,
  focusQuestion,
  formatElapsed,
  formatSurveyNumber,
  isHexColor,
  languageName,
  localize,
  localizeSurveyText,
  mediaSrc,
  next,
  otherKey,
  pickLanguage,
  pipe,
  reachedEnding,
  resolve,
  screenedOutBy,
  surveyLanguages,
  pageErrors,
  pages as pagesOf,
  pause,
  progress,
  resume,
  setAnswer,
  startSession,
  toSubmission,
  type Definition,
  type FlowCounts,
  checkDefinition,
  flowKey,
  randomizerGroups,
  uuid,
  type Question,
  type Session,
  type Submission,
  type Theme,
  THEME_ZOOMS,
} from '@survey/core';
import { ArrowLeft, ArrowRight, Check, CircleCheckBig, Hand, Monitor, RotateCcw, Send, Smartphone, SquareArrowOutUpRight, Tablet, Timer as TimerIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mediaUrl, type FileAnswer } from '../api/client';
import { useStoredState } from '../hooks/useStoredState';
import { useI18n } from '../i18n';
import { InfoTip } from './InfoTip';
import { QuestionInput } from './QuestionInput';
import { QuestionMedia } from './QuestionMedia';
import { alignStyle, RichText } from './RichText';
import { TurnstileWidget, type TurnstileHandle } from './TurnstileWidget';

interface Props {
  definition: Definition;
  survey: { code: string; version: number; title: string; description?: string; number?: number };
  /** Preview mode never submits and shows a banner. */
  preview?: boolean;
  /** Persist the in-progress session here so a reload resumes the same response. */
  storageKey?: string;
  /** Builder route for the preview banner's "open in new tab" button. */
  openUrl?: string;
  /** Cloudflare Turnstile site key; shows the human check above Submit (never in preview). */
  captchaSiteKey?: string;
  /** Quotas that are already full; matching respondents are screened out. */
  fullQuotas?: string[];
  /** Respondents given each section of a balanced or limited randomizer so far. */
  flowCounts?: FlowCounts;
  /** Asks the server for this respondent's sections of balanced randomizers (and records them); null keeps the local pick. */
  claimPicks?: (submissionId: string) => Promise<Record<string, string[]> | null>;
  /** Preview only: the questionnaire has changes respondents don't see until it is published again. */
  unpublished?: boolean;
  /** Hidden-field values from the link (only declared fields are kept). */
  embedded?: Record<string, string>;
  /** Uploads a respondent's file; preview mode fakes it. */
  uploader?: (file: File) => Promise<FileAnswer>;
  onSubmit?: (s: Submission, captchaToken?: string) => Promise<void>;
}

type Phase = 'welcome' | 'answering' | 'review' | 'submitting' | 'done' | 'screened';

const RTL = new Set(['ar', 'he', 'fa', 'ur']);

const FONTS: Record<NonNullable<Theme['font']>, string> = {
  system: 'inherit',
  serif: "Georgia, 'Times New Roman', serif",
  rounded: "ui-rounded, 'SF Pro Rounded', 'Nunito', system-ui, sans-serif",
  mono: "ui-monospace, 'SFMono-Regular', Menlo, monospace",
};

/** CSS variables for the questionnaire's theme. */
export function themeStyle(theme: Theme | undefined): React.CSSProperties {
  const style: Record<string, string> = {};
  if (!theme) return style;
  const c = theme.primaryColor;
  if (isHexColor(c)) {
    style['--accent'] = c!;
    style['--accent-strong'] = c!;
    style['--accent-soft'] = `color-mix(in srgb, ${c} 14%, var(--surface))`;
    style['--accent-text'] = `color-mix(in srgb, ${c} 65%, var(--text))`;
    style['--on-accent'] = readableOn(c!);
  }
  if (theme.font && theme.font !== 'system') style.fontFamily = FONTS[theme.font];
  // Display size chosen for this questionnaire (Settings → Look); only the offered sizes are honoured.
  if (typeof theme.zoom === 'number' && theme.zoom !== 1 && THEME_ZOOMS.includes(theme.zoom)) style.zoom = String(theme.zoom);
  return style as React.CSSProperties;
}

function readableOn(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  return 0.299 * r + 0.587 * g + 0.114 * b > 160 ? '#111111' : '#ffffff';
}

/** Stand-in upload for previews: nothing leaves the browser. */
async function fakeUpload(file: File): Promise<FileAnswer> {
  const ext = (file.name.split('.').pop() ?? 'bin').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 5) || 'bin';
  return { url: `/api/uploads/${'0'.repeat(32)}.${ext}`, name: file.name, size: file.size, type: file.type };
}

function loadSession(key?: string): Session | null {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

function saveSession(key: string | undefined, s: Session | null) {
  if (!key) return;
  try {
    if (s) localStorage.setItem(key, JSON.stringify(s));
    else localStorage.removeItem(key);
  } catch {
    // storage full/unavailable: the response still works, it just won't resume after reload
  }
}

export function SurveyRunner({ definition: baseDef, survey, preview, openUrl, storageKey, captchaSiteKey, fullQuotas, flowCounts, claimPicks, unpublished, embedded, uploader, onSubmit }: Props) {
  const [device, setDevice] = useStoredState<'desktop' | 'tablet' | 'mobile'>('survey.preview.device', 'desktop');
  const captchaOn = !!captchaSiteKey && !preview;
  const { locale: uiLocale } = useI18n();
  const languages = surveyLanguages(baseDef);
  const [lang, setLang] = useState(() => loadSession(storageKey)?.language ?? pickLanguage(baseDef, [typeof navigator !== 'undefined' ? navigator.language : undefined, uiLocale]));
  const def = useMemo(() => localize(baseDef, lang), [baseDef, lang]);
  const text = localizeSurveyText(baseDef, lang, survey.title, survey.description ?? '');
  const placement = def.settings.captcha ?? 'both';
  const captchaAtStart = captchaOn && placement !== 'end';
  const captchaAtEnd = captchaOn && placement !== 'start';
  // Holds the current widget's token; the start and end widgets never show at the same time.
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRef = useRef<TurnstileHandle>(null);
  const [session, setSession] = useState<Session | null>(() => loadSession(storageKey));
  const [phase, setPhase] = useState<Phase>(() => {
    const s = loadSession(storageKey);
    return s ? (s.currentId === null ? 'review' : 'answering') : 'welcome';
  });
  // Reopening the link continues the unfinished response (same answers and randomizer picks); say so, and offer a fresh start.
  const [resumed, setResumed] = useState(() => !preview && !!loadSession(storageKey));
  // Validation is re-run at render time so the message follows the current language.
  const [showError, setShowError] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** Final message and optional redirect, worked out when the response finishes. */
  const [finish, setFinish] = useState<{ message: string; redirect?: string } | null>(null);
  const { t, locale } = useI18n();
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const update = useCallback(
    (s: Session | null) => {
      setSession(s);
      saveSession(storageKey, s);
    },
    [storageKey],
  );

  // Resumed sessions start paused-time from now, not from when the tab closed.
  useEffect(() => {
    if (sessionRef.current && !sessionRef.current.segment) update(resume(sessionRef.current));
    else if (sessionRef.current?.segment) update(resume(pause(sessionRef.current, sessionRef.current.segment.since)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Time spent with the tab hidden is not counted toward response timing.
  useEffect(() => {
    const onVis = () => {
      const s = sessionRef.current;
      if (!s) return;
      update(document.hidden ? pause(s) : resume(s));
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [update]);

  const [starting, setStarting] = useState(false);
  async function start() {
    // The start check only gates the questions; Submit gets its own token.
    setCaptchaToken(null);
    const submissionId = uuid();
    // Randomizers that show sections evenly or cap them ask the server, which counts everyone who started.
    let picks: Record<string, string[]> | null = null;
    if (claimPicks && !preview && def.settings.randomizers?.some((r) => r.evenly || (r.limits && Object.keys(r.limits).length))) {
      setStarting(true);
      picks = await claimPicks(submissionId).catch(() => null);
      setStarting(false);
    }
    const s = startSession(def, { code: survey.code, version: survey.version }, { submissionId, embedded, flowCounts, picks: picks ?? undefined, language: languages.length > 1 ? lang : undefined });
    update(s);
    setPhase(s.currentId === null ? 'review' : 'answering');
  }

  function restart() {
    setResumed(false);
    update(null);
    setShowError(false);
    setSubmitError(null);
    setFinish(null);
    setPhase('welcome');
  }

  /** Text with {{…}} placeholders filled from this response. */
  function piped(text: string, s: Session) {
    return pipe(text, { questions: resolve(def, s.answers).questions, answers: s.answers, embedded: s.embedded, locale });
  }

  function screenOut(s: Session, message: string | undefined) {
    update(null);
    setFinish({ message: message ? piped(message, s) : t('runner.screened_body') });
    setPhase('screened');
  }

  function goNext() {
    if (!session) return;
    setResumed(false);
    const step = next(def, session, Date.now(), locale);
    setShowError(!!step.error);
    if (step.error) {
      focusFirstError(Object.keys(step.errors)[0]);
      return;
    }
    const quota = screenedOutBy(def, step.session, fullQuotas);
    if (quota && !preview) {
      screenOut(step.session, quota.message);
      return;
    }
    update(step.session);
    window.scrollTo?.({ top: 0 });
    if (step.session.currentId === null) setPhase('review');
  }

  function goBack() {
    if (!session) return;
    setShowError(false);
    update(back(def, session));
    setPhase('answering');
  }

  async function submit() {
    if (!session) return;
    const payload = toSubmission(def, session);
    const ending = reachedEnding(def, session);
    const message = ending?.message || def.thankYouMessage;
    const done = { message: message ? piped(message, session) : '', redirect: ending?.redirectUrl };
    if (preview || !onSubmit) {
      setFinish(done);
      setPhase('done');
      return;
    }
    setPhase('submitting');
    setSubmitError(null);
    try {
      // Without a visible check at the end, the API client runs an invisible one.
      await onSubmit(payload, captchaAtEnd ? (captchaToken ?? undefined) : undefined);
      update(null);
      setFinish(done);
      setPhase('done');
      if (done.redirect) setTimeout(() => window.location.assign(done.redirect!), 2500);
    } catch (e) {
      const err = e as { code?: string; details?: { quotaId?: string } };
      if (err.code === 'quota_full') {
        screenOut(session, def.quotas?.find((q) => q.id === err.details?.quotaId)?.message);
        return;
      }
      setSubmitError(e instanceof Error ? e.message : t('runner.submit_failed'));
      setPhase('review');
      // The token was spent on this attempt; get a new one for the retry.
      captchaRef.current?.reset();
    }
  }

  const page = session && session.currentId ? currentPage(def, session, locale) : [];
  const single = page.length === 1;
  const prog = session ? progress(def, session) : { index: 0, total: def.questions.length, fraction: 0, questions: def.questions.length };
  const errors = showError && session ? pageErrors(def, session, locale) : {};
  // Positions of the page's questions among all visible questions, for "Question n of m".
  const firstNumber = session && page.length ? questionNumber(def, session, page[0]!.id) : 0;
  const pageSection = page.length > 1 ? page[0]!.sectionTitle?.trim() : undefined;
  const isLastPage = prog.index + 1 === prog.total;

  function answer(q: Question, v: Parameters<typeof setAnswer>[2], key = q.id) {
    if (!session) return;
    update(setAnswer(focusQuestion(session, q.id), key, v));
  }

  const logo = baseDef.theme?.logoUrl;
  return (
    <div className={`runner${preview && device !== 'desktop' ? ` device-${device}` : ''}`} style={themeStyle(baseDef.theme)} dir={RTL.has(lang.split('-')[0]!) ? 'rtl' : undefined} lang={lang}>
      {preview && (
        <div className="banner mb-16">
          <span className="grow">{t('runner.preview_banner')}</span>
          {openUrl && (
            <a className="btn ghost sm row gap-4" href={openUrl} target="_blank" rel="noreferrer" title={t('runner.open_tab')}>
              <SquareArrowOutUpRight className="icon-sm" /> {t('runner.open_tab')}
            </a>
          )}
          <div className="seg preview-devices" role="group" aria-label={t('all.device')}>
            {(
              [
                ['desktop', Monitor],
                ['tablet', Tablet],
                ['mobile', Smartphone],
              ] as const
            ).map(([id, Icon]) => (
              <button key={id} type="button" className={device === id ? 'active' : ''} aria-pressed={device === id} title={t(`all.device_${id}`)} aria-label={t(`all.device_${id}`)} onClick={() => setDevice(id)}>
                <Icon />
              </button>
            ))}
          </div>
          <button className="btn sm" onClick={restart}>
            <RotateCcw /> {t('runner.restart')}
          </button>
        </div>
      )}
      {preview && <PreviewNotes def={def} session={session} unpublished={unpublished} />}
      <div className="card runner-card">
        {logo && <img className="runner-logo" src={logo.startsWith('/') ? mediaUrl(logo) : mediaSrc(logo)} alt="" />}
        {def.settings.showProgress && phase !== 'welcome' && phase !== 'done' && (
          <div className="runner-progress" aria-label={t('runner.progress', { n: Math.min(prog.index + 1, prog.total), total: prog.total })}>
            <span style={{ width: `${Math.round(prog.fraction * 100)}%` }} />
          </div>
        )}

        {resumed && (phase === 'answering' || phase === 'review') && (
          <div className="banner runner-resumed" role="status">
            <span className="grow">{t('runner.resumed')}</span>
            <button type="button" className="btn sm" onClick={restart}>
              <RotateCcw /> {t('runner.start_over')}
            </button>
          </div>
        )}

        {phase === 'welcome' && (
          <div className="runner-body">
            {preview && (
              <div className="eyebrow">
                {t('runner.eyebrow')} {survey.number ? `${formatSurveyNumber(survey.number)} · ` : '· '}
                {survey.code}
              </div>
            )}
            <h1 className="runner-title">{text.title || t('common.untitled_questionnaire')}</h1>
            {text.description && <p className="runner-desc">{text.description}</p>}
            {def.welcomeMessage && <p className="runner-desc">{pipe(def.welcomeMessage, { questions: [], answers: {}, embedded })}</p>}
            {languages.length > 1 && (
              <label className="field runner-lang">
                <span className="label">{t('runner.language')}</span>
                <select className="select" value={lang} onChange={(e) => setLang(e.target.value)}>
                  {languages.map((l) => (
                    <option key={l} value={l}>
                      {languageName(l)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <p className="small muted">
              {t('runner.question_count', { count: def.questions.length })}
            </p>
            {captchaAtStart && <TurnstileWidget siteKey={captchaSiteKey!} onToken={setCaptchaToken} />}
            <div className="btn-row mt-16">
              <button className="btn accent" style={{ height: 44, padding: '0 20px' }} onClick={() => void start()} disabled={starting || def.questions.length === 0 || (captchaAtStart && !captchaToken)}>
                {t('runner.start')} <ArrowRight />
              </button>
            </div>
          </div>
        )}

        {phase === 'answering' && page.length > 0 && session && (
          <form
            className="runner-body"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              goNext();
            }}
          >
            <div className="eyebrow">
              {single
                ? t('runner.progress', { n: firstNumber, total: prog.questions })
                : prog.total > 1
                  ? t('runner.page_progress', { n: prog.index + 1, total: prog.total })
                  : t('runner.page_questions', { count: prog.questions })}
              {single && page[0]!.questionCode && <span className="pill" style={{ marginLeft: 8 }}>{page[0]!.questionCode}</span>}
              {single && page[0]!.sectionTitle && <span className="pill blue" style={{ marginLeft: 8 }}>{page[0]!.sectionTitle}</span>}
              {single && page[0]!.loop && <span className="pill" style={{ marginLeft: 8 }}>{page[0]!.loop.label}</span>}
              {single && page[0]!.required && page[0]!.type !== 'descriptive' && <span className="pill" style={{ marginLeft: 8 }}>{t('common.required')}</span>}
              {def.settings.showTimer && <Timer session={session} />}
            </div>
            {pageSection && <h2 className="runner-section">{pageSection}</h2>}
            {page.map((q, i) => (
              <QuestionBlock
                key={q.id}
                question={q}
                number={single ? undefined : firstNumber + i}
                showSection={!single && !pageSection && !!q.sectionTitle}
                value={session.answers[q.id]}
                seed={session.submissionId}
                other={session.answers[otherKey(q.id)]}
                onOtherChange={(v) => answer(q, v, otherKey(q.id))}
                uploader={preview ? fakeUpload : uploader}
                error={errors[q.id] ?? null}
                autoFocus={i === 0}
                onFocus={() => {
                  const s = sessionRef.current;
                  if (s && s.segment?.questionId !== q.id) update(focusQuestion(s, q.id));
                }}
                onChange={(v) => {
                  if (single) setShowError(false);
                  answer(q, v);
                }}
              />
            ))}
            <div className="runner-nav">
              {def.settings.allowBack && prog.index > 0 ? (
                <button type="button" className="btn" onClick={goBack}>
                  <ArrowLeft /> {t('common.back')}
                </button>
              ) : (
                <span />
              )}
              <button className="btn primary" style={{ height: 42, padding: '0 18px' }}>
                {isLastPage ? t('runner.finish') : t('common.next')} <ArrowRight />
              </button>
            </div>
          </form>
        )}

        {(phase === 'review' || phase === 'submitting') && (
          <div className="runner-body">
            <div className="tile" style={{ width: 44, height: 44, borderRadius: 12 }}>
              <Check />
            </div>
            {def.settings.showTimer && session && (
              <div className="eyebrow mt-16">
                <Timer session={session} />
              </div>
            )}
            <h2 className="runner-question mt-16">{t('runner.all_done')}</h2>
            <p className="runner-desc">{t('runner.submit_hint')}</p>
            {submitError && (
              <div className="banner error mt-16" role="alert">
                <span className="grow">{submitError}</span>
              </div>
            )}
            {captchaAtEnd && <TurnstileWidget ref={captchaRef} siteKey={captchaSiteKey!} onToken={setCaptchaToken} />}
            <div className="runner-nav">
              {def.settings.allowBack ? (
                <button type="button" className="btn" onClick={goBack} disabled={phase === 'submitting'}>
                  <ArrowLeft /> {t('common.back')}
                </button>
              ) : (
                <span />
              )}
              <button className="btn accent" style={{ height: 42, padding: '0 18px' }} onClick={submit} disabled={phase === 'submitting' || (captchaAtEnd && !captchaToken)}>
                {phase === 'submitting' ? t('runner.submitting') : preview ? t('runner.finish_preview') : t('runner.submit')} <Send />
              </button>
            </div>
          </div>
        )}

        {phase === 'screened' && (
          <div className="runner-body" style={{ textAlign: 'center', alignItems: 'center' }}>
            <div className="tile" style={{ width: 52, height: 52, borderRadius: 16 }}>
              <Hand />
            </div>
            <h2 className="runner-question mt-16">{t('runner.screened_title')}</h2>
            <p className="runner-desc">{finish?.message}</p>
            {preview && (
              <button className="btn mt-16" onClick={restart}>
                <RotateCcw /> {t('runner.run_again')}
              </button>
            )}
          </div>
        )}

        {phase === 'done' && (
          <div className="runner-body" style={{ textAlign: 'center', alignItems: 'center' }}>
            <div className="tile" style={{ width: 52, height: 52, borderRadius: 16, background: 'var(--accent)', color: 'var(--on-accent)' }}>
              <CircleCheckBig />
            </div>
            <h2 className="runner-question mt-16">{preview ? t('runner.preview_complete') : t('runner.thank_you')}</h2>
            {finish?.message && <p className="runner-desc">{finish.message}</p>}
            <p className={finish?.message ? 'small muted' : 'runner-desc'}>{preview ? t('runner.nothing_saved') : finish?.message ? '' : t('runner.recorded')}</p>
            {finish?.redirect && (
              <p className="small muted">
                {preview ? t('runner.would_redirect', { url: finish.redirect }) : t('runner.redirecting')}{' '}
                {!preview && <a href={finish.redirect}>{t('runner.continue')}</a>}
              </p>
            )}
            {preview && (
              <button className="btn mt-16" onClick={restart}>
                <RotateCcw /> {t('runner.run_again')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** 1-based position of a question among the currently visible questions. */
function questionNumber(def: Definition, s: Session, id: string): number {
  let n = 0;
  for (const page of pagesOf(def, s)) for (const q of page) {
    n++;
    if (q === id) return n;
  }
  return n;
}

function focusFirstError(id: string | undefined) {
  if (!id) return;
  const el = document.getElementById(`q-${id}-block`);
  el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  const input = el?.querySelector<HTMLElement>('input, textarea, select, button');
  input?.focus({ preventScroll: true });
}

interface BlockProps {
  question: Question;
  /** Shown on multi-question pages; undefined on single-question pages. */
  number?: number;
  showSection: boolean;
  value: Parameters<typeof setAnswer>[2] | undefined;
  seed: string;
  other: Parameters<typeof setAnswer>[2] | undefined;
  onOtherChange: (v: string) => void;
  uploader?: (file: File) => Promise<FileAnswer>;
  error: string | null;
  autoFocus: boolean;
  onFocus: () => void;
  onChange: (v: Parameters<typeof setAnswer>[2]) => void;
}

function QuestionBlock({ question: q, number, showSection, value, seed, other, onOtherChange, uploader, error, autoFocus, onFocus, onChange }: BlockProps) {
  const { t } = useI18n();
  const multi = number !== undefined;
  return (
    <div id={`q-${q.id}-block`} className={multi ? 'runner-block' : undefined} onFocusCapture={onFocus}>
      {multi && (
        <div className="eyebrow">
          {t('runner.question_n', { n: number })}
          {q.questionCode && <span className="pill" style={{ marginLeft: 8 }}>{q.questionCode}</span>}
          {showSection && <span className="pill blue" style={{ marginLeft: 8 }}>{q.sectionTitle}</span>}
          {q.loop && <span className="pill" style={{ marginLeft: 8 }}>{q.loop.label}</span>}
          {q.required && q.type !== 'descriptive' && <span className="pill" style={{ marginLeft: 8 }}>{t('common.required')}</span>}
        </div>
      )}
      {q.type === 'descriptive' && q.titleRich ? (
        // Formatted instructions can hold paragraphs and lists, which don't belong inside a heading.
        <div className={multi ? 'runner-question sm rich-title' : 'runner-question rich-title'} id={`q-${q.id}-title`} style={alignStyle(q.textAlign)}>
          <RichText source={q.titleRich} />
          {q.tooltip && <InfoTip text={q.tooltip} label={t('runner.about', { title: q.title })} />}
        </div>
      ) : (
        <h2 className={multi ? 'runner-question sm' : 'runner-question'} id={`q-${q.id}-title`} style={alignStyle(q.textAlign)}>
          {q.type === 'descriptive' ? q.title : <label htmlFor={`q-${q.id}`}>{q.titleRich ? <RichText source={q.titleRich} inline /> : q.title}</label>}
          {q.tooltip && <InfoTip text={q.tooltip} label={t('runner.about', { title: q.title })} />}
        </h2>
      )}
      {q.descriptionRich ? (
        <div style={alignStyle(q.textAlign)}>
          <RichText className="runner-desc" source={q.descriptionRich} />
        </div>
      ) : (
        q.description && (
          <p className="runner-desc" style={alignStyle(q.textAlign)}>
            {q.description}
          </p>
        )
      )}
      {q.media && <QuestionMedia key={q.id} media={q.media} />}
      <div className={multi ? 'mt-12' : 'mt-16'}>
        <QuestionInput question={q} value={value} seed={seed} invalid={!!error} autoFocus={autoFocus} onChange={onChange} other={other} onOtherChange={onOtherChange} uploader={uploader} />
      </div>
      {error && (
        <p className="error-text mt-8" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * In the preview: what each randomizer does on this run (which of its sections were picked), or why
 * one is not applied, and whether respondents still see an older published version.
 */
function PreviewNotes({ def, session, unpublished }: { def: Definition; session: Session | null; unpublished?: boolean }) {
  const { t, locale } = useI18n();
  const list = (def.settings.randomizers ?? []).map((r, i) => ({ r, n: i + 1 })).filter(({ r }) => r.sections.length > 0);
  if (!list.length && !unpublished) return null;
  const applied = new Set(randomizerGroups(def).map((g) => g.randomizer.id));
  // The randomizer problems are the issues that only exist because of the randomizers.
  const without = new Set(checkDefinition({ ...def, settings: { ...def.settings, randomizers: undefined } }, locale).map((i) => i.message));
  const problems = checkDefinition(def, locale).filter((i) => !without.has(i.message));
  return (
    <div className="preview-notes mb-16">
      {unpublished && <p className="preview-note warn">{t('runner.preview_unpublished')}</p>}
      {list.map(({ r, n }) => {
        if (!applied.has(r.id)) {
          return (
            <div key={r.id} className="preview-note warn" role="alert">
              <strong>{t('runner.preview_rz_off', { n })}</strong>
              {problems.map((p, k) => (
                <div key={k} className="small">{p.message}</div>
              ))}
            </div>
          );
        }
        const picked = session ? (session.answers[flowKey(r.id)] as string[] | undefined) : undefined;
        return (
          <p key={r.id} className="preview-note">
            {picked?.length
              ? t('runner.preview_rz_picked', { n, sections: picked.join(', '), present: r.present, total: r.sections.length })
              : t('runner.preview_rz_will', { n, present: r.present, total: r.sections.length, sections: r.sections.join(', ') })}
          </p>
        );
      })}
    </div>
  );
}

/** Elapsed active time (FR-11), shown when the questionnaire enables it. Stops while the tab is hidden. */
function Timer({ session }: { session: Session }) {
  const { t } = useI18n();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return (
    <span className="runner-timer" aria-label={t('runner.time_spent', { time: formatElapsed(elapsedMs(session, now)) })} role="timer">
      <TimerIcon aria-hidden /> {formatElapsed(elapsedMs(session, now))}
    </span>
  );
}
